import { observer } from 'mobx-react';

import { store } from 'Models/Store';
import { CargoCharacteristicDto, EffectiveCargoCharacteristicDto } from 'Modules/Cargo';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { transparentTagStyle } from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/CheckInList/CheckInListRowTags';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import Tag from 'Views/Components/_HumanWritten/Tag/Tag';
import { Flex } from 'Views/Components/Flex/Flex';

interface CargoCharacteristicTagsProps {
	effectiveCharacteristics?: EffectiveCargoCharacteristicDto[];
	cargoCharacteristicMap: Record<string, CargoCharacteristicDto>;
	/**
	 * When true, only render tags the current viewer is allowed to see:
	 * staff see characteristics flagged `showToStaff`, customers see `showToCustomer`.
	 * Defaults to false so existing (staff-only) usages keep showing every tag.
	 */
	respectVisibility?: boolean;
}

function CargoCharacteristicTags({
	effectiveCharacteristics,
	cargoCharacteristicMap,
	respectVisibility = false,
}: CargoCharacteristicTagsProps) {
	if (!effectiveCharacteristics || effectiveCharacteristics.length === 0) {
		return null;
	}

	// Duplicates (same id appearing multiple times) can legitimately exist in the source
	// data — only render one tag per distinct characteristic.
	const seenIds = new Set<string>();
	const uniqueCharacteristics = effectiveCharacteristics.filter(c => {
		if (seenIds.has(c.id)) return false;
		seenIds.add(c.id);
		return true;
	});

	const enrichedCharacteristics = uniqueCharacteristics
		.map(c => {
			const details = cargoCharacteristicMap[c.id];
			if (!details) return null;
			return {
				...details,
				source: c.source,
			};
		})
		.filter(isNotNullOrUndefined)
		.filter(c => !respectVisibility || (store.isStaff ? c.showToStaff : c.showToCustomer))
		.slice()
		.sort((a, b) => {
			// Sort by abbreviation
			if (a.abbreviation && b.abbreviation) {
				if (a.abbreviation < b.abbreviation) return -1;
				if (a.abbreviation > b.abbreviation) return 1;
			}
			// Fallback to name if abbreviations are equal or missing (though types suggest they exist)
			if (a.name < b.name) return -1;
			if (a.name > b.name) return 1;
			return 0;
		});

	return (
		<>
			{enrichedCharacteristics.map(c => (
				<Tag key={c.id} className="" style={transparentTagStyle(null)}>
					<Flex gap="xxxs" align="center">
						{c.iconName && <Icon name={c.iconName} useMaterialIcons />}
						{c.displayName || c.name}
					</Flex>
				</Tag>
			))}
		</>
	);
}

export default observer(CargoCharacteristicTags);
